/**
 * How it works.
 *
 * Four steps, in the order the product runs them: collect the conditions, drop
 * what is unreachable, rank a first pick plus backups, then let the group vote.
 * The same list renders as a vertical timeline on a phone and as a horizontal
 * track from `lg` up; the icons are decorative and hidden from readers.
 */

import { MapPin, Route, Sparkles, Vote } from 'lucide-react'
import { useTranslation } from 'react-i18next'

/** Icon per step, kept out of the catalog because it is not translatable. */
const STEP_ICONS = [MapPin, Route, Sparkles, Vote]

/** Anchor the hero's secondary action scrolls to. */
export const LANDING_STEPS_ID = 'how-it-works'

export function LandingSteps() {
  const { t } = useTranslation()

  const steps = [
    { title: t('home.stepCollectTitle'), body: t('home.stepCollectBody') },
    { title: t('home.stepFilterTitle'), body: t('home.stepFilterBody') },
    { title: t('home.stepRecommendTitle'), body: t('home.stepRecommendBody') },
    { title: t('home.stepVoteTitle'), body: t('home.stepVoteBody') },
  ]

  return (
    <section
      id={LANDING_STEPS_ID}
      className="landing-section scroll-mt-20"
      aria-labelledby="landing-steps-title"
    >
      <div className="landing-container flex flex-col gap-5">
        <h2
          id="landing-steps-title"
          className="max-w-3xl text-2xl font-semibold tracking-tight text-balance sm:text-3xl"
        >
          {t('home.stepsTitle')}
        </h2>

        <ol className="grid gap-2 lg:grid-cols-4">
          {steps.map((step, index) => {
            const Icon = STEP_ICONS[index] ?? MapPin
            return (
              <li
                key={step.title}
                className="border-border bg-card relative flex gap-3 rounded-2xl border p-2.5 lg:flex-col lg:gap-2.5"
              >
                <span className="bg-accent text-accent-foreground flex size-9 shrink-0 items-center justify-center rounded-xl">
                  <Icon className="size-4" aria-hidden />
                </span>
                {/* The step number shares the title's line: on its own line it
                    cost a row of height per step for one two-digit label. */}
                <div className="flex min-w-0 flex-col gap-1">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-muted-foreground text-xs font-medium tabular-nums">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <h3 className="text-sm font-semibold">{step.title}</h3>
                  </div>
                  <p className="text-muted-foreground text-sm">{step.body}</p>
                </div>
              </li>
            )
          })}
        </ol>
      </div>
    </section>
  )
}
